import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchThreadsMeta } from '../api/threadsClient';
import { INBOX_UPDATED_EVENT } from './threads';

/**
 * El número del nav: HILOS con respuestas sin leer MÁS invitaciones sin
 * contestar.
 *
 * Es la suma de las dos poblaciones porque el inbox lista las dos, y el filtro
 * "No leídos" de esa pantalla muestra exactamente esa suma. Un badge que no se
 * puede vaciar entrando a la pantalla que promete es un badge roto — de ahí que
 * NO haya un segundo badge en ninguna otra ruta: `pending_invitations` saldría
 * contado dos veces, y de ahí también que `total_unread` cuente hilos y no
 * mensajes: tres respuestas de la misma empresa son UNA fila en la lista.
 *
 * Se refresca en tres momentos y en ninguno más:
 *   1. al montar el shell;
 *   2. al cambiar de ruta (navegar es la señal barata de "algo pudo cambiar");
 *   3. con el evento `tbit:inbox-updated`, que dispara el detalle del hilo
 *      cuando abrirlo dejó mensajes leídos y el número ya quedó viejo.
 *
 * Sin polling: hoy no hay push, y un intervalo golpeando /meta cada N segundos
 * gasta batería para un número que casi nunca cambia entre dos clics.
 *
 * `enabled` apaga las tres: durante el alta no hay inbox que contar —el badge
 * ni siquiera se dibuja— y la llamada sólo serviría para gastar una petición
 * (y un 400 `no_membership`) en el momento más frágil del alta.
 */
export function useInboxBadge(enabled = true): number {
  const [count, setCount] = useState(0);
  const { pathname } = useLocation();

  const refresh = useCallback(() => {
    if (!enabled) return;
    fetchThreadsMeta()
      .then((meta) => setCount((meta.total_unread ?? 0) + (meta.pending_invitations ?? 0)))
      // El badge es cromo: si /meta se cae, el nav se queda sin número, no con
      // un error. Nunca vale la pena romper la barra por un contador.
      .catch(() => {});
  }, [enabled]);

  useEffect(refresh, [refresh, pathname]);

  useEffect(() => {
    window.addEventListener(INBOX_UPDATED_EVENT, refresh);
    return () => window.removeEventListener(INBOX_UPDATED_EVENT, refresh);
  }, [refresh]);

  return count;
}
